import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

interface SqlProgress {
 completedLessons: string[];
 solvedProblems: string[];
}

interface UseSqlProgressReturn {
 progress: SqlProgress;
 markLessonComplete: (lessonId: string) => void;
 markProblemSolved: (problemId: string) => void;
 isLessonComplete: (lessonId: string) => boolean;
 isProblemSolved: (problemId: string) => boolean;
}

const STORAGE_KEY = 'sql-progress';
const DEBOUNCE_MS = 500;

function loadFromStorage(): SqlProgress {
 try {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw) {
   const parsed = JSON.parse(raw);
   return {
    completedLessons: Array.isArray(parsed.completedLessons) ? parsed.completedLessons : [],
    solvedProblems: Array.isArray(parsed.solvedProblems) ? parsed.solvedProblems : [],
   };
  }
 } catch {
  // ignore parse errors
 }
 return { completedLessons: [], solvedProblems: [] };
}

function saveToStorage(progress: SqlProgress): void {
 try {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
 } catch {
  // ignore storage errors
 }
}

export function useSqlProgress(): UseSqlProgressReturn {
 const { state } = useAuth();
 const isAuthenticated = state.isAuthenticated;

 const [progress, setProgress] = useState<SqlProgress>(() => loadFromStorage());
 const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
 const pendingPatchRef = useRef<Partial<SqlProgress> | null>(null);

 // Fetch progress from API on mount when authenticated
 useEffect(() => {
  if (!isAuthenticated) return;

  let cancelled = false;

  (async () => {
   try {
    const data = await api.get('/progress/sql');
    if (!cancelled) {
     const fetched: SqlProgress = {
      completedLessons: Array.isArray(data.completedLessons) ? data.completedLessons : [],
      solvedProblems: Array.isArray(data.solvedProblems) ? data.solvedProblems : [],
     };
     setProgress(fetched);
     saveToStorage(fetched);
    }
   } catch {
    // Graceful degradation — operate from local state
   }
  })();

  return () => {
   cancelled = true;
  };
 }, [isAuthenticated]);

 // Debounced PATCH to backend
 const schedulePatch = useCallback(
  (patch: Partial<SqlProgress>) => {
   if (!isAuthenticated) return;

   // Merge pending patches
   pendingPatchRef.current = {
    ...pendingPatchRef.current,
    ...(patch.completedLessons && { completedLessons: patch.completedLessons }),
    ...(patch.solvedProblems && { solvedProblems: patch.solvedProblems }),
   };

   if (debounceRef.current) {
    clearTimeout(debounceRef.current);
   }

   debounceRef.current = setTimeout(async () => {
    const payload = pendingPatchRef.current;
    pendingPatchRef.current = null;
    if (!payload) return;

    try {
     await api.patch('/progress/sql', payload);
    } catch {
     // Swallow error — graceful degradation
    }
   }, DEBOUNCE_MS);
  },
  [isAuthenticated]
 );

 const markLessonComplete = useCallback(
  (lessonId: string) => {
   // Support uncompleting: if lessonId starts with __UNCOMPLETE__ prefix, remove it
   if (lessonId.startsWith('__UNCOMPLETE__')) {
    const realId = lessonId.replace('__UNCOMPLETE__', '');
    setProgress((prev) => {
     const updated = {
      ...prev,
      completedLessons: prev.completedLessons.filter((id) => id !== realId),
     };
     saveToStorage(updated);
     return updated;
    });
    // Sync removal to backend
    if (isAuthenticated) {
     api.delete('/progress/sql', { body: JSON.stringify({ completedLessons: [realId] }), headers: { 'Content-Type': 'application/json' } }).catch(() => { });
    }
    return;
   }
   setProgress((prev) => {
    if (prev.completedLessons.includes(lessonId)) return prev;
    const updated = {
     ...prev,
     completedLessons: [...prev.completedLessons, lessonId],
    };
    saveToStorage(updated);
    schedulePatch({ completedLessons: [lessonId] });
    return updated;
   });
  },
  [schedulePatch]
 );

 const markProblemSolved = useCallback(
  (problemId: string) => {
   setProgress((prev) => {
    if (prev.solvedProblems.includes(problemId)) return prev;
    const updated = {
     ...prev,
     solvedProblems: [...prev.solvedProblems, problemId],
    };
    saveToStorage(updated);
    schedulePatch({ solvedProblems: [problemId] });
    return updated;
   });
  },
  [schedulePatch]
 );

 const isLessonComplete = useCallback(
  (lessonId: string) => progress.completedLessons.includes(lessonId),
  [progress.completedLessons]
 );

 const isProblemSolved = useCallback(
  (problemId: string) => progress.solvedProblems.includes(problemId),
  [progress.solvedProblems]
 );

 // Cleanup debounce timer on unmount
 useEffect(() => {
  return () => {
   if (debounceRef.current) {
    clearTimeout(debounceRef.current);
   }
  };
 }, []);

 return {
  progress,
  markLessonComplete,
  markProblemSolved,
  isLessonComplete,
  isProblemSolved,
 };
}
